<template>
  <div class="flex flex-col w-full h-full pt-4 pb-8 bg-white">
    <h2 class="text-2xl sm:text-3xl font-black text-slate-900 mb-2 leading-tight text-center sm:text-right">כמה צעדים אחרונים 🚀</h2>
    <p class="text-slate-500 mb-8 text-center sm:text-right">
      {{ isThirdPartySignUp ? 'אנחנו צריכים קצת פרטים עלייך ועל העסק שלך כדי להתחיל.' : 'ספרו לנו קצת על העסק שלכם כדי שנוכל להקים אותו בשבילכם.' }}
    </p>

    <div class="space-y-5">
      <!-- Full Name (Third Party) -->
      <div v-if="isThirdPartySignUp" class="flex flex-col gap-2">
        <label class="text-sm font-bold text-slate-700" for="fullName">שם מלא</label>
        <InputText
          id="fullName"
          v-model="fullName"
          type="text"
          placeholder="השם המלא שלך..."
          class="w-full !bg-slate-50 !border !border-slate-200 !text-slate-900 !rounded-xl !py-3.5 focus:!outline-none focus:!border-pink-500 focus:!ring-1 focus:!ring-pink-500 transition-all"
        />
        <small v-if="isThirdPartySignUp" v-show="fullNameError !== ''" class="text-red-500 text-xs mt-1 font-medium">{{ fullNameError }}</small>
      </div>

      <!-- Business Name -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-bold text-slate-700" for="businessName">שם העסק</label>
        <InputText
          id="businessName"
          v-model="businessName"
          type="text"
          placeholder="לדוגמה: שירן ביוטי קליניק"
          class="w-full !bg-slate-50 !border !border-slate-200 !text-slate-900 !rounded-xl !py-3.5 focus:!outline-none focus:!border-pink-500 focus:!ring-1 focus:!ring-pink-500 transition-all"
        />
        <small v-show="businessNameError !== ''" class="text-red-500 text-xs mt-1 font-medium">{{ businessNameError }}</small>
      </div>

      <!-- Business Category Picker -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-bold text-slate-700">סוג העסק <span class="font-normal text-slate-400">(ניתן לבחור יותר מאחד)</span></label>
        <div class="grid grid-cols-3 sm:grid-cols-4 gap-2">
          <button
            v-for="cat in categoryOptions"
            :key="cat.value"
            type="button"
            class="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center cursor-pointer"
            :class="businessCategories.includes(cat.value)
              ? 'border-pink-500 bg-pink-50 text-pink-700'
              : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200 hover:bg-slate-100'"
            @click="toggleCategory(cat.value)"
          >
            <component :is="cat.icon" class="w-5 h-5" />
            <span class="text-[11px] sm:text-xs font-medium leading-tight">{{ cat.label }}</span>
          </button>
        </div>
        <small v-show="businessCategoryError !== ''" class="text-red-500 text-xs mt-1 font-medium">{{ businessCategoryError }}</small>
      </div>

      <!-- Business Size Picker -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-bold text-slate-700">גודל העסק</label>
        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="size in sizeOptions"
            :key="size.value"
            type="button"
            class="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center cursor-pointer"
            :class="businessSize === size.value
              ? 'border-pink-500 bg-pink-50 text-pink-700'
              : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200 hover:bg-slate-100'"
            @click="businessSize = size.value"
          >
            <component :is="size.icon" class="w-5 h-5" />
            <span class="text-xs font-bold">{{ size.label }}</span>
            <span class="text-[10px] text-slate-400">{{ size.description }}</span>
          </button>
        </div>
      </div>

      <!-- Current Booking Method -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-bold text-slate-700">איך קובעים אצלך תורים היום?</label>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="method in bookingMethodOptions"
            :key="method.value"
            type="button"
            class="px-4 py-2.5 rounded-full border-2 text-xs font-medium transition-all cursor-pointer"
            :class="currentBookingMethod === method.value
              ? 'border-pink-500 bg-pink-50 text-pink-700'
              : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200'"
            @click="currentBookingMethod = method.value"
          >
            {{ method.label }}
          </button>
        </div>
      </div>

      <!-- Location -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-bold text-slate-700" for="location">כתובת העסק</label>
        <InputText
          id="location"
          v-model="location"
          type="text"
          placeholder="הכניסי כתובת מלאה..."
          class="w-full !bg-slate-50 !border !border-slate-200 !text-slate-900 !rounded-xl !py-3.5 focus:!outline-none focus:!border-pink-500 focus:!ring-1 focus:!ring-pink-500 transition-all"
        />
        <small v-show="locationError !== ''" class="text-red-500 text-xs mt-1 font-medium">{{ locationError }}</small>
      </div>

      <!-- Business Phone -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-bold text-slate-700" for="phone">מספר טלפון של העסק</label>
        <p class="text-xs text-slate-500 mb-1">אליו נשלח הודעת אימות ב-SMS, שימו לב שהוא זמין.</p>
        <InputMask
          id="phone"
          v-model="businessPhoneNumber"
          inputmode="numeric"
          mask="999-999-9999"
          pattern="[0-9]*"
          placeholder="050-000-0000"
          class="w-full !bg-slate-50 !border !border-slate-200 !text-slate-900 !rounded-xl !py-3.5 focus:!outline-none focus:!border-pink-500 focus:!ring-1 focus:!ring-pink-500 transition-all text-right"
          dir="ltr"
        />
        <small v-show="businessPhoneNumberError !== ''" class="text-red-500 text-xs mt-1 font-medium">{{ businessPhoneNumberError }}</small>
      </div>

      <!-- Unique ID (for Landing Page URL) -->
      <div class="flex flex-col gap-2 bg-pink-50/50 p-4 rounded-2xl border border-pink-100 mt-2">
        <label class="text-sm font-bold text-slate-700" for="uniqueId">שם העסק באנגלית (עבור הקישור שלכם)</label>
        <p class="text-xs text-slate-500 mb-1">אותיות באנגלית בלבד (ניתן לשים רווחים). ללא מספרים או סימנים.</p>
        <InputText
          id="uniqueId"
          v-model="uniqueId"
          type="text"
          placeholder="לדוגמה: shiran beauty"
          class="w-full !bg-white !border !border-slate-200 !text-slate-900 !rounded-xl !py-3.5 focus:!outline-none focus:!border-pink-500 focus:!ring-1 focus:!ring-pink-500 transition-all text-left"
          dir="ltr"
        />
        <small v-show="uniqueIdError !== ''" class="text-red-500 text-xs mt-1 font-medium">{{ uniqueIdError }}</small>

        <div class="mt-3 bg-white p-3 sm:p-4 rounded-xl border border-slate-100 flex flex-col gap-1 shadow-sm">
          <span class="text-xs font-bold text-slate-500">ככה ייראה דף הנחיתה היוקרתי שלך:</span>
          <span class="text-sm text-pink-600 font-mono truncate font-medium" dir="ltr">{{ formattedUrlExample }}</span>
        </div>
      </div>

      <!-- Referrer (Third Party) -->
      <template v-if="isThirdPartySignUp">
        <div class="flex flex-col gap-2 mt-4">
          <label class="text-sm font-bold text-slate-700" for="referer">איך שמעת עלינו?</label>
          <Select
            v-model="referer"
            :options="referrers"
            optionLabel="name"
            optionValue="value"
            placeholder="בחרי תשובה"
            class="w-full !bg-slate-50 !border-slate-200 !rounded-xl"
            :pt="{ root: { class: '!border-slate-200 focus:!border-pink-500' } }"
          />
        </div>

        <div v-if="referer === 'other' || referer === 'friend'" class="flex flex-col gap-2 transition-all">
          <label class="text-sm font-bold text-slate-700" for="refererOptions">נשמח לעוד קצת פרטים ☺️</label>
          <InputText
            id="refererOptions"
            v-model="refererOptions"
            type="text"
            placeholder="כתבי לנו כאן..."
            class="w-full !bg-slate-50 !border !border-slate-200 !text-slate-900 !rounded-xl !py-3.5 focus:!outline-none focus:!border-pink-500 focus:!ring-1 focus:!ring-pink-500 transition-all"
          />
        </div>
      </template>
    </div>

    <!-- Submit Button -->
    <Button
      v-wave
      label="סיום והקמת העסק"
      :loading="loading"
      class="w-full mt-8 !bg-gradient-to-r !from-pink-500 !to-purple-600 hover:!from-pink-400 hover:!to-purple-500 !text-white !font-bold !py-4 !rounded-xl !border-0 transition-all !shadow-lg shadow-pink-500/30 transform hover:-translate-y-1 flex flex-none items-center justify-center text-lg"
      @click="createBusiness"
    />

    <div class="h-12 flex-none"></div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from 'vue-toastification';
import validatePhone from 'phone';
import {
  IBusinessSiteInfo,
  Plans,
  BusinessCategoryEnum,
  BusinessSizeEnum,
  CurrentBookingMethodEnum,
  businessCategoryLabels,
  businessSizeLabels,
  currentBookingMethodLabels,
} from '@torim-app/types';
import { AdminApi } from '@app/api/admin/admin-api';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Select from 'primevue/select';
import InputText from 'primevue/inputtext';
import InputMask from 'primevue/inputmask';
import Button from 'primevue/button';
import { trackEvent } from '@app/composables/posthog';
import {
  Scissors,
  Sparkles,
  Hand,
  Eye,
  Palette,
  Flower2,
  PenTool,
  HeartPulse,
  EyeOff,
  Waves,
  Star,
  Stethoscope,
  Activity,
  Dumbbell,
  MoreHorizontal,
  User,
  Users,
  Building,
} from 'lucide-vue-next';

const toast = useToast();
const router = useRouter();
const route = useRoute();

const isThirdPartySignUp = route.query.origin === 'third-party';
const referralCode = route.query['referral-code'] as string;

const fbclid = route.query.fbclid as string;
const fbc = fbclid ? `fb.1.${Date.now()}.${fbclid}` : document.cookie.match(/_fbc=([^;]+)/)?.[1];

// Which landing page design this visitor saw, and their acquisition source — both forwarded
// from the marketing site's goTo() hand-off — attached to `business_created` below so the
// signups dashboard can break conversions down by variant and by source.
const landingVariant = route.query.landing_variant as string;
const landingExperiment = route.query.landing_experiment as string;
const landingRevision = route.query.landing_revision as string;
const landingMode = route.query.landing_mode as string;
const utmSource = route.query.utm_source as string;
const utmMedium = route.query.utm_medium as string;
const utmCampaign = route.query.utm_campaign as string;
const referrers = [
  { name: 'פייסבוק / אינסטגרם', value: 'meta' },
  { name: 'גוגל', value: 'google' },
  { name: 'חבר/ה', value: 'friend' },
  { name: 'אחר', value: 'other' }
];

const categoryOptions = [
  { value: BusinessCategoryEnum.BARBER, label: businessCategoryLabels[BusinessCategoryEnum.BARBER], icon: Scissors },
  { value: BusinessCategoryEnum.HAIR_STYLIST, label: businessCategoryLabels[BusinessCategoryEnum.HAIR_STYLIST], icon: Sparkles },
  { value: BusinessCategoryEnum.NAILS, label: businessCategoryLabels[BusinessCategoryEnum.NAILS], icon: Hand },
  { value: BusinessCategoryEnum.LASHES, label: businessCategoryLabels[BusinessCategoryEnum.LASHES], icon: Eye },
  { value: BusinessCategoryEnum.MAKEUP, label: businessCategoryLabels[BusinessCategoryEnum.MAKEUP], icon: Palette },
  { value: BusinessCategoryEnum.SKINCARE, label: businessCategoryLabels[BusinessCategoryEnum.SKINCARE], icon: Flower2 },
  { value: BusinessCategoryEnum.TATTOO, label: businessCategoryLabels[BusinessCategoryEnum.TATTOO], icon: PenTool },
  { value: BusinessCategoryEnum.MASSAGE, label: businessCategoryLabels[BusinessCategoryEnum.MASSAGE], icon: HeartPulse },
  { value: BusinessCategoryEnum.EYEBROWS, label: businessCategoryLabels[BusinessCategoryEnum.EYEBROWS], icon: EyeOff },
  { value: BusinessCategoryEnum.WAXING, label: businessCategoryLabels[BusinessCategoryEnum.WAXING], icon: Waves },
  { value: BusinessCategoryEnum.COSMETICIAN, label: businessCategoryLabels[BusinessCategoryEnum.COSMETICIAN], icon: Star },
  { value: BusinessCategoryEnum.OTHER, label: businessCategoryLabels[BusinessCategoryEnum.OTHER], icon: MoreHorizontal },
];

const sizeOptions = [
  { value: BusinessSizeEnum.SOLO, label: businessSizeLabels[BusinessSizeEnum.SOLO], description: 'רק אני', icon: User },
  { value: BusinessSizeEnum.SMALL_TEAM, label: businessSizeLabels[BusinessSizeEnum.SMALL_TEAM], description: '2-4 אנשים', icon: Users },
  { value: BusinessSizeEnum.SALON, label: businessSizeLabels[BusinessSizeEnum.SALON], description: '5 ומעלה', icon: Building },
];

const bookingMethodOptions = [
  { value: CurrentBookingMethodEnum.WHATSAPP, label: currentBookingMethodLabels[CurrentBookingMethodEnum.WHATSAPP] },
  { value: CurrentBookingMethodEnum.PHONE, label: currentBookingMethodLabels[CurrentBookingMethodEnum.PHONE] },
  { value: CurrentBookingMethodEnum.OTHER_APP, label: currentBookingMethodLabels[CurrentBookingMethodEnum.OTHER_APP] },
  { value: CurrentBookingMethodEnum.NONE, label: currentBookingMethodLabels[CurrentBookingMethodEnum.NONE] },
];

const loading = ref(false);
const businessName = ref('');
const location = ref('');
const businessPhoneNumber = ref('');
const uniqueId = ref('');
const businessCategories = ref<BusinessCategoryEnum[]>([]);
const toggleCategory = (cat: BusinessCategoryEnum) => {
  const idx = businessCategories.value.indexOf(cat);
  if (idx === -1) businessCategories.value.push(cat);
  else businessCategories.value.splice(idx, 1);
};
const businessSize = ref<BusinessSizeEnum | null>(null);
const currentBookingMethod = ref<CurrentBookingMethodEnum | null>(null);

const businessNameError = ref('');
const businessPhoneNumberError = ref('');
const locationError = ref('');
const uniqueIdError = ref('');
const businessCategoryError = ref('');

const fullName = ref('');
const referer = ref('');
const fullNameError = ref('');
const refererOptions = ref('');

const formattedUrlExample = computed<string>(() => {
  return `${import.meta.env.VITE_BUSINESS_WEBSITE_DOMAIN}/business/${
    uniqueId.value ? formatUniqueId() : 'XXXXXXXXXX'
  }`;
});

const formatUniqueId = (): string => {
  return uniqueId.value.toLowerCase().trim().split(' ').join('_');
};

const clearErrors = () => {
  businessNameError.value = '';
  locationError.value = '';
  businessPhoneNumberError.value = '';
  uniqueIdError.value = '';
  fullNameError.value = '';
  businessCategoryError.value = '';
};

const createBusiness = async () => {
  loading.value = true;
  clearErrors();

  let isErrorState = false;
  if (isThirdPartySignUp && (fullName.value === '' || fullName.value.length < 2)) {
    fullNameError.value = 'צריך להיות לפחות 2 תווים';
    isErrorState = true;
  }

  if (!businessName.value || businessName.value.length < 3) {
    businessNameError.value = 'חובה להזין שם בעל 4 אותיות לפחות';
    isErrorState = true;
  }
  if (!businessPhoneNumber.value || businessPhoneNumber.value === '' || !validatePhone(businessPhoneNumber.value, { country: 'IL' }).isValid) {
    businessPhoneNumberError.value = 'מספר הטלפון שהוזן לא תקין';
    isErrorState = true;
  }
  if (!location.value || location.value.length < 8) {
    locationError.value = 'חובה להזין כתובת תקינה';
    isErrorState = true;
  }
  if (
    !uniqueId.value ||
    !/^[a-zA-Z\s]+$/.test(uniqueId.value) ||
    uniqueId.value.length < 3
  ) {
    uniqueIdError.value =
      ' שם העסק באנגלית חייב להכיל לפחות 4 אותיות ובאנגלית בלבד';
    isErrorState = true;
  }
  if (!businessCategories.value.length) {
    businessCategoryError.value = 'חובה לבחור סוג עסק';
    isErrorState = true;
  }

  if (isErrorState) {
    loading.value = false;
    return;
  }

  const bid = formatUniqueId();

  try {
    window.fbq?.('track', 'CompleteRegistration', {}, { eventID: `${bid}_CompleteRegistration` });
    console.log('track CompleteRegistration');
  } catch (e) {
    console.error(`Failed to send report, ${e}`)
  }

  try {
    window.oaiq?.('measure', 'registration_completed', { type: 'customer_action' });
  } catch (e) {
    console.error(`Failed to send OpenAI report, ${e}`)
  }

  const plan = Plans.PREMIUM;
  const body: IBusinessSiteInfo & { referer?: string; fullName?: string; referralCode?: string; fbc?: string; businessCategory?: BusinessCategoryEnum[]; businessSize?: BusinessSizeEnum; currentBookingMethod?: CurrentBookingMethodEnum } = {
    bid: formatUniqueId(),
    location: location.value,
    title: businessName.value,
    phone: businessPhoneNumber.value,
    referer: isThirdPartySignUp ? (refererOptions.value ?? referer.value) : undefined,
    fullName: isThirdPartySignUp ? fullName.value : undefined,
    plan,
    referralCode,
    fbc: fbc || undefined,
    businessCategory: businessCategories.value.length ? businessCategories.value : undefined,
    businessSize: businessSize.value || undefined,
    currentBookingMethod: currentBookingMethod.value || undefined,
  };
  try {
    await AdminApi.createBasicBusiness(body);
    trackEvent('business_created', {
      plan,
      businessCategory: businessCategories.value,
      businessSize: businessSize.value,
      landingVariant,
      landingExperiment,
      landingRevision,
      landingMode,
      utmSource,
      utmMedium,
      utmCampaign,
    });

    // Store onboarding context for the AI setup step
    sessionStorage.setItem('onboarding-context', JSON.stringify({
      businessName: businessName.value,
      businessCategory: businessCategories.value,
      businessSize: businessSize.value,
      currentBookingMethod: currentBookingMethod.value,
    }));

    await router.replace({
      query: { step: 'validate' }
    });

  } catch (e) {
    trackEvent('business_creation_failed');
    toast.error('לא הצלחנו לרשום אותך, ננסה שוב?');
  }
  loading.value = false;

};
</script>

